import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import {
  ArrowRight,
  Download,
  FileText,
  FileSpreadsheet,
  FileType2,
  Lock,
  Loader2,
} from 'lucide-react';
import { api, useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const iconForFile = (filename) => {
  const ext = (filename || '').split('.').pop()?.toLowerCase();
  if (ext === 'xls' || ext === 'xlsx' || ext === 'csv') return FileSpreadsheet;
  if (ext === 'doc' || ext === 'docx') return FileType2;
  return FileText;
};

const Welcome = () => {
  const { t } = useTranslation();
  return (
    <section id="willkommen" className="bg-[#1B3A5C] py-20 lg:py-28 text-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#b8893d] rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#3a6fa0] rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-4xl">
          <h2 className="font-serif font-light text-3xl md:text-4xl lg:text-5xl leading-tight mb-8">
            {t('welcome.headline_line1', 'Kanzlei Nabhan Gäbler & Partner')}<br />{t('welcome.headline_line2', 'heißt Sie willkommen')}
          </h2>
          <p className="text-white/85 text-lg leading-relaxed mb-10 max-w-3xl">
            {t('welcome.body', 'Mit Kanzlei Nabhan Gäbler & Partner ist Ihr Fall immer oberste Priorität. Wir betreuen Privatpersonen ebenso wie Unternehmerinnen und Unternehmer und haben zahlreiche Menschen erfolgreich auf ihrem Weg zur deutschen Staatsangehörigkeit begleitet. Seit 2021 vertreten wir unsere Mandanten in allen Spezialgebieten – persönlich, mehrsprachig, vollständig digital.')}
          </p>
          <Button className="bg-[#b8893d] hover:bg-[#a07730] text-white px-8 py-6 text-sm tracking-[0.2em] font-medium rounded-sm group">
            {t('welcome.cta', 'MEHR ERFAHREN')}
            <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </div>
    </section>
  );
};

const Services = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { toast } = useToast();

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get('/downloads/catalog');
        if (!alive) return;
        setCategories(data.categories || []);
      } catch (_e) {
        if (alive) setCategories([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const allDocs = useMemo(
    () => categories.flatMap((c) => c.documents.map((d) => ({ ...d, _cat: c.category }))),
    [categories]
  );

  const visibleDocs = allDocs.filter((d) => d.available).slice(0, 3);


  const handleDownload = async (doc) => {
    if (!doc.available) return;
    if (!isAuthenticated) {
      navigate(`/login?next=${encodeURIComponent('/downloads')}`);
      return;
    }
    setDownloading(doc.slug);
    try {
      const response = await api.get(`/downloads/file/${doc.slug}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      const ext = (doc.filename || '').split('.').pop() || 'pdf';
      link.href = url;
      link.setAttribute('download', `${doc.label}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast({
        title: t('services_section.download_failed', 'Download fehlgeschlagen'),
        description:
          e?.response?.data?.detail ||
          t('services_section.try_again', 'Bitte versuchen Sie es später erneut.'),
        variant: 'destructive',
      });
    } finally {
      setDownloading(null);
    }
  };

  return (
    <section id="downloads" className="bg-white py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-10">
          <div className="flex items-center gap-3 text-[#b8893d] text-xs tracking-[0.3em] mb-4">
            <span className="w-12 h-px bg-[#b8893d]" aria-hidden="true"></span>
            {t('services_section.eyebrow', 'MANDANTENBEREICH')}
          </div>
          <h2 className="font-serif font-light text-[#1B3A5C] text-4xl md:text-5xl tracking-tight mb-4">
            {t('services_section.headline', 'Vorlagen & Downloads')}
          </h2>
          <p className="text-[#34536b] leading-relaxed">
            {t(
              'services_section.subline',
              'Geprüfte Formulare, Checklisten und Mustervorlagen unserer Kanzlei. Der Download ist unseren Mandantinnen und Mandanten vorbehalten.'
            )}
          </p>
        </div>

        {/* Card grid */}
        {loading ? (
          <div className="text-center py-16" role="status" aria-live="polite">
            <Loader2 className="w-8 h-8 text-[#b8893d] animate-spin mx-auto" aria-hidden="true" />
            <span className="sr-only">{t('services_section.loading', 'Wird geladen…')}</span>
          </div>
        ) : visibleDocs.length === 0 ? (
          <div className="bg-[#f5f8fa] p-12 text-center rounded-sm border border-dashed border-gray-200">
            <FileText className="w-10 h-10 text-[#b8893d]/40 mx-auto mb-3" aria-hidden="true" />
            <p className="text-[#34536b] text-sm">
              {t('services_section.empty', 'In dieser Kategorie sind aktuell keine Dokumente verfügbar.')}
            </p>
          </div>
        ) : (
          <ul
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
            data-testid="downloads-home-grid"
          >
            {visibleDocs.map((doc) => {
              const Icon = iconForFile(doc.filename);
              const isDownloading = downloading === doc.slug;
              const ext = (doc.filename || '').split('.').pop()?.toUpperCase() || 'PDF';
              return (
                <li key={doc.slug}>
                  <article
                    className="h-full flex flex-col bg-white border border-gray-200 rounded-sm p-5 hover:border-[#1B3A5C] hover:shadow-md transition-all focus-within:border-[#1B3A5C] focus-within:shadow-md"
                    data-testid={`download-card-${doc.slug}`}
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div
                        className={`w-11 h-11 rounded-sm flex items-center justify-center flex-shrink-0 ${
                          doc.available ? 'bg-[#fbf7f0]' : 'bg-gray-100'
                        }`}
                        aria-hidden="true"
                      >
                        <Icon
                          className={`w-5 h-5 ${doc.available ? 'text-[#b8893d]' : 'text-gray-400'}`}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-[#1B3A5C] font-semibold text-base leading-snug mb-1">
                          {doc.label}
                        </h3>
                        <span className="block text-[10px] tracking-wider text-[#34536b] truncate" title={doc._cat}>
                          {doc._cat}
                        </span>
                      </div>
                    </div>

                    {doc.description && (
                      <p className="text-[#34536b] text-xs leading-relaxed mb-4 line-clamp-2">
                        {doc.description}
                      </p>
                    )}

                    <div className="mt-auto flex items-center justify-between gap-3 pt-3 border-t border-gray-100">
                      <div className="flex items-center gap-2 text-[10px] text-[#34536b] tracking-wider uppercase">
                        <span className="font-semibold">{ext}</span>
                        {doc.available && doc.size > 0 && (
                          <>
                            <span aria-hidden="true">·</span>
                            <span>{formatSize(doc.size)}</span>
                          </>
                        )}
                        {!doc.available && (
                          <span className="bg-[#b8893d]/10 text-[#b8893d] px-2 py-0.5 rounded-sm normal-case tracking-wider">
                            {t('services_section.coming_soon', 'Bald verfügbar')}
                          </span>
                        )}
                      </div>
                      <Button
                        type="button"
                        onClick={() => handleDownload(doc)}
                        disabled={!doc.available || isDownloading}
                        aria-label={
                          !doc.available
                            ? t('services_section.unavailable_aria', 'Nicht verfügbar')
                            : !isAuthenticated
                            ? t('services_section.login_required_aria', 'Anmelden um {{label}} herunterzuladen', { label: doc.label })
                            : t('services_section.download_aria', '{{label}} herunterladen', { label: doc.label })
                        }
                        data-testid={`download-home-btn-${doc.slug}`}
                        className="bg-[#b8893d] hover:bg-[#a07730] text-white tracking-wider text-[11px] px-4 disabled:opacity-40 focus-visible:ring-2 focus-visible:ring-[#1B3A5C] focus-visible:ring-offset-2"
                      >
                        {isDownloading ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />
                        ) : !isAuthenticated && doc.available ? (
                          <>
                            <Lock className="w-3.5 h-3.5 mr-1.5" aria-hidden="true" />
                            {t('services_section.login_short', 'ANMELDEN')}
                          </>
                        ) : (
                          <>
                            <Download className="w-3.5 h-3.5 mr-1.5" aria-hidden="true" />
                            {t('services_section.download_short', 'HERUNTERLADEN')}
                          </>
                        )}
                      </Button>
                    </div>
                  </article>
                </li>
              );
            })}
          </ul>
        )}

        {!loading && (
          <div className="mt-10">
            <Link to="/downloads" data-testid="downloads-home-alle"
              className="inline-flex items-center gap-2 bg-[#1B3A5C] hover:bg-[#0f2740] text-white text-xs tracking-[0.15em] uppercase px-6 py-3 rounded-sm transition-colors focus-visible:ring-2 focus-visible:ring-[#b8893d] focus-visible:ring-offset-2">
              {t('services_section.alle_ansehen')} <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export { Welcome, Services };
